import React, { useState, useRef, useEffect } from "react";
import CommonTable from "components/ReactTable/Table";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  CardTitle,
  Col,
  Container,
  Input,
  Row,
  Badge,
} from "reactstrap";
import NotificationAlert from "react-notification-alert";
import { DEFAULT_RECORDS_PER_PAGE } from "components/Common/constants";
import { dashboardOdooQuotationsNotSyncedList } from "actions/dashboard";
import { useNavigate } from "react-router-dom";

function OdooQuotationsList() {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const [pageNum, setPageNum] = useState(1);
  const [quotationListState, setQuotationListState] = useState({
    isLoading: true,
    error: "",
    noRecords: false,
    showLoaderOnClick: false,
  });
  
  // ✅ Separated state for Work Order ID and Customer/Outlet search
  const [filterState, setFilterState] = useState({
    searchWorkOrderID: "",   // Sent via filters
    searchName: "",          // Sent via searchString
    filterParams: [],
  });

  // ✅ Local state for the Odoo API response
  const [odooResponse, setOdooResponse] = useState(null);

  // --- Build filter params from UI state ---
  const buildFilterParams = () => {
    const filterParams = [];

    // ✅ Work Order ID filter (sent via filters)
    if (filterState.searchWorkOrderID.trim()) {
      filterParams.push({
        field: "WorkOrderID",
        condition: "eq",
        filterValues: [filterState.searchWorkOrderID.trim()],
      });
    }

    return {
      filters:
        filterParams.length > 0 ? JSON.stringify(filterParams) : undefined,
      searchString: filterState.searchName || "",
    };
  };

  // --- Fetch Odoo not-synced quotations list ---
  useEffect(() => {
    const fetchData = async () => {
      setQuotationListState((prev) => ({ ...prev, isLoading: true }));

      const filters = filterState.filterParams?.filters;
      const searchString = filterState.filterParams?.searchString || "";

      try {
        const res = await dashboardOdooQuotationsNotSyncedList({
          pageNum,
          searchString,
          filters:
            filters && filters !== "[]" && filters.length > 2
              ? filters
              : undefined,
        });
        setOdooResponse(res?.data || res);
      } catch (err) {
        setOdooResponse({
          code: 500,
          message: "Failed to load quotations list! Please try again.",
          data: null,
        });
      }
    };

    fetchData();
  }, [pageNum, filterState.filterParams]);

  // --- Sync loader / error state ---
  useEffect(() => {
    if (!odooResponse) return;

    if (odooResponse.code === 200) {
      setQuotationListState({
        isLoading: false,
        error: "",
        noRecords: (odooResponse?.data?.quotation?.length ?? 0) === 0,
        showLoaderOnClick: false,
      });
    } else if (odooResponse.code || odooResponse.message) {
      setQuotationListState({
        isLoading: false,
        error:
          odooResponse.message ||
          "Failed to load quotations list! Please try again.",
        noRecords: false,
        showLoaderOnClick: false,
      });
    }
  }, [odooResponse]);

  const formattedQuotationList =
    odooResponse?.data?.quotation?.map((item, index) => ({
      ...item,
      // Fallback to first_name if company_name is empty
      customerName:
        item.company_name?.trim() ||
        `${item.first_name || ""} ${item.last_name || ""}`.trim() ||
        "-",
      srNo:
        (pageNum - 1) *
          (odooResponse?.data?.no_of_records_per_page ||
            DEFAULT_RECORDS_PER_PAGE) +
        index +
        1,
    })) ?? [];

  // Handle the API's snake_case keys
  const totalRecords =
    odooResponse?.data?.total_records ?? odooResponse?.data?.totalRecords ?? 0;
  const totalFilteredRecords =
    odooResponse?.data?.total_filtered_records ??
    odooResponse?.data?.totalFilteredRecords ??
    0;
  const noOfRecordsPerPage =
    odooResponse?.data?.no_of_records_per_page ??
    odooResponse?.data?.noOfRecordsPerPage ??
    DEFAULT_RECORDS_PER_PAGE;

  const handleSearch = () => {
    // Validate: at least one filter must be filled
    if (
      !filterState.searchWorkOrderID.trim() &&
      !filterState.searchName.trim()
    ) {
      return;
    }

    const newParams = buildFilterParams();

    if (
      JSON.stringify(filterState.filterParams) !== JSON.stringify(newParams)
    ) {
      setQuotationListState((prev) => ({
        ...prev,
        isLoading: true,
        showLoaderOnClick: true,
      }));
      setFilterState((prev) => ({ ...prev, filterParams: newParams }));
      if (pageNum !== 1) setPageNum(1);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSearch();
    }
  };

  const handleClear = () => {
    setQuotationListState((prev) => ({ ...prev, isLoading: true }));
    setFilterState({
      searchWorkOrderID: "",
      searchName: "",
      filterParams: [],
    });
    if (pageNum !== 1) setPageNum(1);
  };

  // --- Table columns mapped to the API response ---
  const columns = [
    { Header: "Sr. no.", accessor: "srNo", className: "col-discount-srNo", centerAlign: true },
    {
      Header: "Work Order",
      accessor: "workOrderID",
      className: "col-job-id",
      Cell: ({ value }) => <span>{value ?? "-"}</span>,
    },
    {
      Header: "Customer",
      accessor: "customerName",
      className: "col-cust-name",
      Cell: ({ row }) => (
        <div>
          {row.original.firstName === "" && row.original.lastName === "" && "-"}{" "}
          {row.original.firstName} {row.original.lastName}
        </div>
      ),
    },
    {
      Header: "Outlet",
      accessor: "outletName",
      className: "col-outlet",
      Cell: ({ value }) => <span>{value || "-"}</span>,
    },
    {
      Header: "Grand Total (SAR)",
      accessor: "grandTotal",
      className: "col-grand-total",
      Cell: ({ value }) => (
        <span className="font-weight-bold">
          {value != null ? value.toLocaleString() : "-"}
        </span>
      ),
    },
  ];

  return (
    <>
      <Container fluid className="mt-4">
        <div className="rna-container">
          <NotificationAlert ref={notificationAlertRef} />
        </div>

        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="mb-2">
                <div className="d-flex flex-column align-items-start">
                  <CardTitle tag="h3" className="font-weight-normal">
                    Odoo Quotations — Not Synced
                  </CardTitle>
                </div>
              </CardHeader>

              <CardBody>
                {/* Filters */}
                <div className="d-flex align-items-center justify-content-between flex-wrap flex-column flex-md-row">
                  <div className="d-flex flex-wrap align-items-center filter-group">
                    {/* ✅ Work Order ID → goes into filters */}
                    <Input
                      type="text"
                      placeholder="Search by Work Order ID"
                      className="mr-2 mb-2 filter-input-small"
                      value={filterState.searchWorkOrderID}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchWorkOrderID: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    {/* ✅ Customer/Outlet → goes into searchString */}
                    <Input
                      type="text"
                      placeholder="Search by customer or outlet"
                      className="mr-2 mb-2 filter-input-large"
                      value={filterState.searchName}
                      onChange={(e) =>
                        setFilterState((prev) => ({
                          ...prev,
                          searchName: e.target.value,
                        }))
                      }
                      onKeyDown={handleKeyDown}
                      maxLength={150}
                    />

                    <Button
                      color="default"
                      className="d-flex align-items-center justify-content-center mb-3 filter-btn btn-simple mt-2 mr-1"
                      onClick={handleSearch}
                    >
                      {quotationListState.showLoaderOnClick ? (
                        <i className="fa fa-spinner fa-spin search-loader" />
                      ) : (
                        <>
                          <i className="fa fa-search mr-1" />
                          Search
                        </>
                      )}
                    </Button>

                    {(filterState.filterParams?.filters?.length > 0 ||
                      filterState.filterParams?.searchString?.trim() ||
                      filterState.searchWorkOrderID.trim()) && (
                      <Button
                        color="default"
                        className="d-flex align-items-center mb-3 filter-btn btn-simple mt-2"
                        onClick={handleClear}
                      >
                        <i className="fa fa-times mr-1" />
                        Clear
                      </Button>
                    )}
                  </div>
                </div>

                {/* Records count */}
                <div className="mt-2 mb-3 mx-1">
                  {totalRecords > 0 &&
                    (filterState.filterParams?.filters?.length > 0 ||
                    filterState.filterParams?.searchString?.trim() ||
                    filterState.searchWorkOrderID.trim() ? (
                      <span className="font-weight-bold">
                        Showing {totalFilteredRecords} of {totalRecords}{" "}
                        Quotations
                      </span>
                    ) : (
                      <span className="font-weight-bold">
                        Total Quotations : {totalRecords}
                      </span>
                    ))}
                </div>

                {/* Table */}
                {quotationListState.isLoading ? (
                  <div className="loaderContainer">
                    <i className="fa fa-spinner fa-spin fa-2xl loader" />
                  </div>
                ) : quotationListState.error ? (
                  <div className="loaderContainer">
                    {quotationListState.error}
                  </div>
                ) : quotationListState.noRecords ? (
                  <div className="loaderContainer text-muted">
                    No quotations found.
                  </div>
                ) : (
                  <div className="table-responsive">
                    <CommonTable
                      showPaginationBottom={true}
                      defaultPageSize={noOfRecordsPerPage}
                      activePage={pageNum}
                      data={formattedQuotationList}
                      resizable={false}
                      columns={columns}
                      className="-striped -highlight table-hover"
                      totalPages={Math.ceil(
                        totalFilteredRecords / noOfRecordsPerPage
                      )}
                      setPageNum={setPageNum}
                    />
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </>
  );
}

export default OdooQuotationsList;